import { Box, SxProps, Theme } from '@mui/material';
import classNames from 'clsx';
import RouteLink, { LinkProps } from 'next/link';
import { useRouter } from 'next/router';
import { endsWith, forEach, isString, startsWith } from 'lodash-es';
import React, { forwardRef, Ref } from 'react';
import { matchPath, PathPattern } from 'react-router';
import urlParse from 'url-parse';
import isInternalDomain from '../../../utils/isInternalDomain';

export interface LocalizedLinkProps extends Omit<LinkProps, 'href'> {
  lang?: string;
  children?: React.ReactNode;
  className?: string;
  forwardRef?: Ref<any>;
  relOverride?: boolean;
  title?: string;
  pathPattern?: PathPattern | string;
  target?: React.HTMLAttributeAnchorTarget;
  style?: React.CSSProperties;
  isDisableLanguagePrefix?: boolean;
  forceRefresh?: boolean;
  disabled?: boolean;
  active?: boolean;
  href?: string;
  rel?: string;
  sx?: SxProps<Theme>;
}

const LocalizedLink = forwardRef<HTMLAnchorElement, LocalizedLinkProps>(
  (props: LocalizedLinkProps, ref) => {
    const { locale = 'en', pathname, asPath } = useRouter();

    const {
      lang = locale,
      children,
      className,
      onClick,
      relOverride,
      title,
      style,
      scroll = true,
      isDisableLanguagePrefix = false,
      prefetch = false,
      forceRefresh,
      shallow = true,
      pathPattern,
      sx,
      active,
      disabled,
    } = props;

    let { href = '' } = props;
    const isActive =
      active || (href && !!matchPath(pathPattern || href, pathname));

    const usesLanguage =
      !isDisableLanguagePrefix && startsWith('/', href) && lang !== 'en';
    if (usesLanguage) {
      href = `/${lang}${href}`;
    }

    let { target, rel = [] } = props;

    if (isString(rel)) {
      rel = [rel];
    }

    const parsedUrl = urlParse(href, true);
    const hostname = parsedUrl.hostname;

    if (!target) {
      if (hostname && !isInternalDomain(hostname)) {
        let isReferrerOk = false;

        // @ts-ignore
        forEach((domain) => {
          if (endsWith(domain as unknown as string, hostname)) {
            isReferrerOk = true;
            return false;
          }
          // }, configs?.links?.referrerOk || []);
        });

        // @ts-ignore
        rel.push('noopener nofollow');
        if (!isReferrerOk) {
          // @ts-ignore
          rel.push('noreferrer');
        }

        target = '_blank';
      }
    }

    const anchorProps = {
      ref,
      onClick,
      target,
      // rel={relOverride || rel.join(' ') || ''}
      href,
      title,
      style,
      className: classNames(className),
      'data-qa-id': `${props['data-qa-id']}`,
    };

    if (disabled) {
      return <>{children}</>;
    }

    const ALink = (
      <a style={{ textDecoration: 'none' }} {...anchorProps} ref={ref}>
        <Box className={classNames(className, { active: isActive })} sx={sx}>
          {children}
        </Box>
      </a>
    );

    const allowDynamicLink = !forceRefresh && href && href[0] === '/';

    return allowDynamicLink ? (
      <RouteLink
        {...anchorProps}
        scroll={scroll}
        shallow={shallow}
        passHref
        ref={ref}
      >
        <Box
          className={classNames(className, { active: isActive })}
          sx={sx}
          component={'span'}
        >
          {children}
        </Box>
      </RouteLink>
    ) : (
      ALink
    );
  },
);

LocalizedLink.displayName = 'LocalizedLink';

export default LocalizedLink;
